import React from 'react';
import { UploadCloud, Download, ExternalLink, X, ChevronRight } from 'lucide-react';
import { Button as MuiButton } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import documentIcon from '@/assets/images/document-icon.svg';
import trashBinIcon from '@/assets/images/trash-bin-trash.svg';
import SlashSearchInput from '@/components/shared/SlashSearchInput';
import { ClientsFilterBar } from '@/clients/list/components/ClientsFilterBar';
import { SearchableSelect } from '@/components/shared/SearchableSelect';
import { Progress } from '@/components/ui/progress';
import { BiomarkerTrendSection } from '@/clients/details/v2/components/analysis/BiomarkerTrendSection';
import { cn } from '@/lib/utils';
import type { Biomarker } from '@/types/client';
import {
  compareClientLabReports,
  getClientLabReportsAllBiomarkers,
  getClientLabReport,
  getClientLabReports,
  getClientLabReportDownloadUrl,
  deleteClientLabReport,
  updateClientLabReportNotes,
  extractClientLabReportMulti,
  validateLabReportFiles,
  LAB_REPORT_ALLOWED_MIME_TYPES,
  type ClientLabReportExtractionProgressState,
  type ClientLabReportSummary,
  type ClientLabReportStatusCounts,
  type ClientLabReportCompareBiomarker,
  type ClientLabReportPanelBiomarker,
} from '@/lib/apiService';
import { showErrorToast, showNotesSavedToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

interface ClientLabReportsSectionProps {
  clientId: string;
  clientName?: string;
  onRefresh?: () => Promise<void> | void;
}

type LabReportsSubview = 'lab-reports' | 'biomarker-panel';

interface DerivedLabReport {
  id: string;
  reportName: string;
  labName: string | null;
  testedDate: string;
  biomarkers: Biomarker[];
  biomarkerCount: number;
  biomarkerPreviewRemainingCount: number;
  tagNames: string[];
  reviewState: 'reviewed' | 'attention';
  notes: string | null;
}

type BiomarkerTrendRecord = Biomarker & {
  previousValue?: number | null;
  /** Exact string from API when `previous.value` was sent as a string. */
  previousValueText?: string | null;
  previousUnit?: string | null;
  /** testedDate of the `previous` record itself, as returned by the API — do not infer this from trendDates. */
  previousTestedDate?: string | null;
  changePercent?: number | null;
  changeDirection?: string | null;
  hasPreviousFromApi?: boolean;
  hasChangePercentFromApi?: boolean;
  trendValues?: number[];
  trendDates?: string[];
  referenceGuideline?: string | null;
  panelCategory?: string | null;
};

const reportBiomarkersSessionCache = new Map<string, Biomarker[]>();
const reportBiomarkersSessionInFlight = new Map<string, Promise<Biomarker[]>>();
const compareBiomarkersSessionCache = new Map<string, ClientLabReportCompareBiomarker[]>();
const compareBiomarkersSessionInFlight = new Map<string, Promise<ClientLabReportCompareBiomarker[]>>();
const compareEnrichedReportsSession = new Set<string>();

function buildReportSessionKey(clientId: string, reportId: string): string {
  return `${clientId}::${reportId}`;
}

function buildCompareSessionKey(clientId: string, leftReportId: string, rightReportId: string): string {
  return `${clientId}::${leftReportId}::${rightReportId}`;
}

function getRangeSource(
  biomarker: Pick<Biomarker, 'rangeSource'>
): { label: 'Report' | 'Internal' | 'No Range' | 'Unknown' } {
  if (biomarker.rangeSource === 'report') {
    return {
      label: 'Report',
    };
  }

  if (biomarker.rangeSource === 'internal') {
    return {
      label: 'Internal',
    };
  }

  if (biomarker.rangeSource === 'none') {
    return {
      label: 'No Range',
    };
  }

  return {
    label: 'Unknown',
  };
}

function formatRangeNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(4)));
}

function getRangeValueText(biomarker: Biomarker): string {
  const referenceRangeText = biomarker.referenceRangeText?.trim();
  if (referenceRangeText) return referenceRangeText;

  const hasLower = typeof biomarker.rangeLowerBound === 'number' && Number.isFinite(biomarker.rangeLowerBound);
  const hasUpper = typeof biomarker.rangeUpperBound === 'number' && Number.isFinite(biomarker.rangeUpperBound);
  const unitSuffix = biomarker.unit ? ` ${biomarker.unit}` : '';

  if (hasLower && hasUpper) {
    return `${formatRangeNumber(biomarker.rangeLowerBound as number)} - ${formatRangeNumber(biomarker.rangeUpperBound as number)}${unitSuffix}`;
  }

  if (hasLower) {
    return `>= ${formatRangeNumber(biomarker.rangeLowerBound as number)}${unitSuffix}`;
  }

  if (hasUpper) {
    return `<= ${formatRangeNumber(biomarker.rangeUpperBound as number)}${unitSuffix}`;
  }

  return '—';
}

function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

const EXTRACTION_PROGRESS_STEPS = [10, 20, 30, 50, 90] as const;
const EXTRACTION_PROGRESS_STEP_INTERVAL_MS = 2000;

function getExtractionProgressLabel(state: ClientLabReportExtractionProgressState | null): string {
  if (state === 'uploading') return 'Uploading file...';
  if (state === 'queued') return 'Queued for extraction...';
  if (state === 'in_progress') return 'Analyzing lab report...';
  if (state === 'completed') return 'Extraction completed.';
  return 'Working...';
}

function formatReportDate(value: string | null | undefined): string {
  if (!value) return 'Unknown date';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return 'Unknown date';
  return new Date(parsed).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getReportTimestamp(report: Pick<ClientLabReportSummary, 'testDate' | 'uploadedAt'>): number {
  const testDateTime = getDateTimestamp(report.testDate);
  if (testDateTime > 0) return testDateTime;
  return getDateTimestamp(report.uploadedAt);
}

function getDateBadgeParts(value: string | null | undefined): { day: string; month: string } {
  if (!value) {
    return { day: '--', month: 'N/A' };
  }
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return { day: '--', month: 'N/A' };
  }
  const date = new Date(parsed);
  return {
    day: String(date.getDate()).padStart(2, '0'),
    month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
  };
}


function normalizeStatus(status: string | null | undefined): string {
  return String(status ?? '').trim().toLowerCase();
}

function isBiomarkerAbnormal(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  if (!normalized || normalized === 'normal') return false;
  return normalized === 'high' || normalized === 'critical' || normalized === 'low' || normalized === 'borderline';
}

function normalizeBiomarkerName(value: string | null | undefined): string {
  return String(value ?? '').trim().replace(/\s+/g, ' ');
}

function getBiomarkerDisplayName(item: Biomarker): string {
  const named = normalizeBiomarkerName(item.biomarkerName);
  if (named) return named;
  return item.biomarkerId || 'Biomarker';
}

function escapeCsvCell(value: unknown): string {
  const normalized = value == null ? '' : String(value);
  return `"${normalized.replace(/"/g, '""')}"`;
}

function downloadCsv(filename: string, rows: Array<Array<unknown>>) {
  const csv = rows.map((row) => row.map((cell) => escapeCsvCell(cell)).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function formatMeasuredValue(value: number | null | undefined, unit: string | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const safeUnit = String(unit ?? '').trim();
  return `${value}${safeUnit ? ` ${safeUnit}` : ''}`;
}

function isAttentionFromStatusCounts(statusCounts: ClientLabReportStatusCounts | undefined): boolean {
  if (!statusCounts) return false;
  return (
    statusCounts.low > 0
    || statusCounts.high > 0
    || statusCounts.borderline > 0
    || statusCounts.critical > 0
  );
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function firstFiniteNumber(values: unknown[]): number | null {
  for (const value of values) {
    const normalized = toFiniteNumber(value);
    if (normalized !== null) return normalized;
  }
  return null;
}

function firstNonEmptyString(values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const normalized = value?.trim();
    if (normalized) return normalized;
  }
  return null;
}

function firstRangeSource(
  values: Array<Biomarker['rangeSource'] | undefined>
): Biomarker['rangeSource'] | null {
  return values.find((value): value is NonNullable<Biomarker['rangeSource']> =>
    value === 'report' || value === 'internal' || value === 'none'
  ) ?? null;
}

function getDateTimestamp(value: string | null | undefined): number {
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : 0;
}

function mapApiBiomarkerToClientBiomarker(
  biomarker: ClientLabReportPanelBiomarker,
  report: ClientLabReportSummary,
  clientId: string
): BiomarkerTrendRecord | null {
  const measuredValue = toFiniteNumber(biomarker.current?.value);
  if (measuredValue === null) return null;
  const previousValue = toFiniteNumber(biomarker.previous?.value);
  const previousRawText = biomarker.previous?.valueText;
  const previousValueText = typeof previousRawText === 'string' && previousRawText.trim()
    ? previousRawText.trim()
    : null;
  const changePercent = toFiniteNumber(biomarker.change?.percent);
  const hasPreviousFromApi = biomarker.previous != null
    && (previousValue !== null || previousValueText !== null);
  const hasChangePercentFromApi = biomarker.change != null && changePercent !== null;
  const testedDate = biomarker.current?.testedDate || report.testDate || report.uploadedAt || new Date().toISOString();
  const trendValues = Array.isArray(biomarker.trendPoints)
    ? biomarker.trendPoints
      .map((point) => toFiniteNumber(point?.value))
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
    : [];
  const trendDates = Array.isArray(biomarker.trendPoints)
    ? biomarker.trendPoints
      .map((point) => point?.testedDate)
      .filter((d): d is string => typeof d === 'string' && Boolean(d))
    : [];
  const referenceGuideline = typeof biomarker.current?.referenceGuideline === 'string'
    && biomarker.current.referenceGuideline.trim()
    ? biomarker.current.referenceGuideline.trim()
    : null;
  const rangeLowerBound = firstFiniteNumber([
    biomarker.current?.rangeLowerBound,
    biomarker.previous?.rangeLowerBound,
    ...biomarker.trendPoints.map((point) => point.rangeLowerBound),
  ]);
  const rangeUpperBound = firstFiniteNumber([
    biomarker.current?.rangeUpperBound,
    biomarker.previous?.rangeUpperBound,
    ...biomarker.trendPoints.map((point) => point.rangeUpperBound),
  ]);
  const referenceRangeText = firstNonEmptyString([
    biomarker.current?.referenceRangeText,
    biomarker.previous?.referenceRangeText,
    ...biomarker.trendPoints.map((point) => point.referenceRangeText),
  ]);
  const rangeSource = firstRangeSource([
    biomarker.current?.rangeSource,
    biomarker.previous?.rangeSource,
    ...biomarker.trendPoints.map((point) => point.rangeSource),
  ]);

  return {
    id: biomarker.id || `${report.id}-${biomarker.biomarkerId}`,
    clientId,
    biomarkerId: biomarker.biomarkerId,
    biomarkerName: biomarker.biomarkerName || biomarker.biomarkerId,
    value: measuredValue,
    unit: biomarker.current?.unit || '',
    status: biomarker.current?.status || 'unknown',
    testedDate,
    addedAt: report.uploadedAt || testedDate,
    labName: report.labName ?? null,
    rangeSource,
    rangeLowerBound,
    rangeUpperBound,
    referenceRangeText,
    referenceGuideline,
    interpretation: biomarker.current?.interpretation || null,
    previousValue,
    previousValueText,
    previousUnit: biomarker.previous?.unit || biomarker.current?.unit || '',
    previousTestedDate: biomarker.previous?.testedDate ?? null,
    changePercent,
    changeDirection: biomarker.change?.direction || null,
    hasPreviousFromApi,
    hasChangePercentFromApi,
    trendValues: trendValues.length > 0 ? trendValues : [measuredValue],
    trendDates: trendDates.length > 0 ? trendDates : undefined,
    panelCategory: biomarker.category ?? null,
  };
}

function hasBiomarkerTrendPayload(row: Biomarker): boolean {
  const source = row as BiomarkerTrendRecord;
  const hasPreviousValue = source.hasPreviousFromApi === true
    && (
      (typeof source.previousValue === 'number' && Number.isFinite(source.previousValue))
      || Boolean(source.previousValueText?.trim())
    );
  const hasChangePercent = source.hasChangePercentFromApi === true
    && typeof source.changePercent === 'number'
    && Number.isFinite(source.changePercent);
  const trendValues = Array.isArray(source.trendValues)
    ? source.trendValues.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
    : [];

  return hasPreviousValue || hasChangePercent || trendValues.length > 1;
}

function enrichBiomarkersWithCompareData(
  biomarkers: Biomarker[],
  compareItems: ClientLabReportCompareBiomarker[]
): Biomarker[] {
  if (!biomarkers.length || !compareItems.length) return biomarkers;

  const compareByBiomarkerId = new Map<string, ClientLabReportCompareBiomarker>();
  compareItems.forEach((item) => {
    if (item.biomarkerId) {
      compareByBiomarkerId.set(item.biomarkerId, item);
    }
  });

  return biomarkers.map((row) => {
    const compareItem = compareByBiomarkerId.get(row.biomarkerId);
    if (!compareItem) return row;

    const source = row as BiomarkerTrendRecord;
    const rightValue = toFiniteNumber(compareItem.right?.value);
    const leftValue = toFiniteNumber(compareItem.left?.value);
    const leftValueText = compareItem.left?.valueText;
    const leftTrimmedText = typeof leftValueText === 'string' && leftValueText.trim()
      ? leftValueText.trim()
      : null;
    const changePercent = toFiniteNumber(compareItem.delta?.percent);
    const keepPanelPrevious = source.hasPreviousFromApi === true;
    const resolvedPreviousValue = keepPanelPrevious
      ? (
        typeof source.previousValue === 'number' && Number.isFinite(source.previousValue)
          ? source.previousValue
          : null
      )
      : leftValue;
    const resolvedPreviousValueText = keepPanelPrevious
      ? (
        typeof source.previousValueText === 'string' && source.previousValueText.trim()
          ? source.previousValueText.trim()
          : null
      )
      : leftTrimmedText;
    const resolvedPreviousUnit = keepPanelPrevious
      ? (source.previousUnit || compareItem.left?.unit || compareItem.right?.unit || row.unit || '')
      : (compareItem.left?.unit || compareItem.right?.unit || source.previousUnit || row.unit || '');
    const resolvedHasPreviousFromApi = keepPanelPrevious
      ? source.hasPreviousFromApi === true
      : leftValue !== null || leftTrimmedText !== null;
    const resolvedPreviousTestedDate = keepPanelPrevious
      ? (source.previousTestedDate ?? null)
      : (compareItem.left?.testedDate ?? null);
    const existingTrend = Array.isArray(source.trendValues)
      ? source.trendValues.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      : [];
    const trendLeft = resolvedPreviousValue ?? (
      typeof source.previousValue === 'number' && Number.isFinite(source.previousValue)
        ? source.previousValue
        : leftValue
    );
    const trendValues = existingTrend.length > 1
      ? existingTrend
      : trendLeft !== null && rightValue !== null
        ? [trendLeft, rightValue]
        : existingTrend.length
          ? existingTrend
          : [row.value];

    const enriched: BiomarkerTrendRecord = {
      ...source,
      // Keep status from the selected report payload; compare data is only for trend/change context.
      status: row.status || 'unknown',
      previousValue: resolvedPreviousValue,
      previousValueText: resolvedPreviousValueText,
      previousUnit: resolvedPreviousUnit,
      previousTestedDate: resolvedPreviousTestedDate,
      changePercent,
      changeDirection: compareItem.delta?.direction || source.changeDirection || null,
      hasPreviousFromApi: resolvedHasPreviousFromApi,
      hasChangePercentFromApi: changePercent !== null,
      trendValues,
    };

    return enriched as unknown as Biomarker;
  });
}

function compareReportDatesDesc(left: Pick<DerivedLabReport, 'testedDate'>, right: Pick<DerivedLabReport, 'testedDate'>): number {
  const leftTime = Date.parse(left.testedDate || '') || 0;
  const rightTime = Date.parse(right.testedDate || '') || 0;
  return rightTime - leftTime;
}

export const ClientLabReportsSection: React.FC<ClientLabReportsSectionProps> = ({
  clientId,
  clientName,
  onRefresh,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const reportBiomarkersRequestRef = React.useRef<Map<string, Promise<Biomarker[]>>>(new Map());
  const compareReportRequestRef = React.useRef<Map<string, Promise<ClientLabReportCompareBiomarker[]>>>(new Map());
  const compareReportCacheRef = React.useRef<Map<string, ClientLabReportCompareBiomarker[]>>(new Map());
  const compareEnrichedReportsRef = React.useRef<Set<string>>(new Set());
  const biomarkerPanelLoadGateRef = React.useRef<Set<string>>(new Set());
  const biomarkerPanelLoadSignatureRef = React.useRef<string | null>(null);
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [validationMessage, setValidationMessage] = React.useState<string | null>(null);
  const [isExtracting, setIsExtracting] = React.useState(false);
  const [extractionProgress, setExtractionProgress] = React.useState<ClientLabReportExtractionProgressState | null>(null);
  const [extractionProgressPercent, setExtractionProgressPercent] = React.useState(0);
  const [apiReports, setApiReports] = React.useState<ClientLabReportSummary[]>([]);
  const [reportBiomarkersById, setReportBiomarkersById] = React.useState<Record<string, Biomarker[]>>({});
  const [allReportsBiomarkers, setAllReportsBiomarkers] = React.useState<Biomarker[]>([]);
  const [activeSubview, setActiveSubview] = React.useState<LabReportsSubview>('lab-reports');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [biomarkerSearchQuery, setBiomarkerSearchQuery] = React.useState('');
  const [selectedReportId, setSelectedReportId] = React.useState('');
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [notesModalReport, setNotesModalReport] = React.useState<DerivedLabReport | null>(null);
  const [notesText, setNotesText] = React.useState('');
  const [notesSaving, setNotesSaving] = React.useState(false);
  const [deleteConfirmReport, setDeleteConfirmReport] = React.useState<DerivedLabReport | null>(null);
  const [isDeletingReport, setIsDeletingReport] = React.useState(false);
  const [isAllReportsExpanded, setIsAllReportsExpanded] = React.useState(false);
  const selectReportErrorShownRef = React.useRef(false);
  const lastReportSelectionFetchRef = React.useRef<string | null>(null);

  const handleBiomarkerSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setBiomarkerSearchQuery(event.target.value);
  }, []);

  const apiReportsById = React.useMemo(
    () => new Map(apiReports.map((item) => [item.id, item])),
    [apiReports]
  );

  const reportBiomarkersByIdRef = React.useRef(reportBiomarkersById);
  React.useEffect(() => {
    reportBiomarkersByIdRef.current = reportBiomarkersById;
  }, [reportBiomarkersById]);

  const getPreviousReportId = React.useCallback((reportId: string): string | null => {
    const index = apiReports.findIndex((item) => item.id === reportId);
    if (index < 0) return null;
    const previous = apiReports[index + 1];
    return previous?.id || null;
  }, [apiReports]);

  const loadCompareBiomarkers = React.useCallback(async (
    leftReportId: string,
    rightReportId: string
  ): Promise<ClientLabReportCompareBiomarker[]> => {
    const compareKey = `${leftReportId}::${rightReportId}`;
    const compareSessionKey = buildCompareSessionKey(clientId, leftReportId, rightReportId);
    const cached = compareBiomarkersSessionCache.get(compareSessionKey) || compareReportCacheRef.current.get(compareKey);
    if (cached) {
      compareReportCacheRef.current.set(compareKey, cached);
      return cached;
    }

    const inFlight = compareBiomarkersSessionInFlight.get(compareSessionKey) || compareReportRequestRef.current.get(compareKey);
    if (inFlight) {
      compareReportRequestRef.current.set(compareKey, inFlight);
      return inFlight;
    }

    const request = (async () => {
      const compareResponse = await compareClientLabReports(clientId, {
        leftReportId,
        rightReportId,
      });
      const normalized = Array.isArray(compareResponse.biomarkers) ? compareResponse.biomarkers : [];
      compareReportCacheRef.current.set(compareKey, normalized);
      compareBiomarkersSessionCache.set(compareSessionKey, normalized);
      return normalized;
    })();

    compareReportRequestRef.current.set(compareKey, request);
    compareBiomarkersSessionInFlight.set(compareSessionKey, request);
    try {
      return await request;
    } finally {
      compareReportRequestRef.current.delete(compareKey);
      compareBiomarkersSessionInFlight.delete(compareSessionKey);
    }
  }, [clientId]);

  const isReportCompareEnriched = React.useCallback((reportId: string): boolean => {
    const reportSessionKey = buildReportSessionKey(clientId, reportId);
    return compareEnrichedReportsRef.current.has(reportId)
      || compareEnrichedReportsSession.has(reportSessionKey);
  }, [clientId]);

  React.useEffect(() => {
    biomarkerPanelLoadGateRef.current.clear();
    biomarkerPanelLoadSignatureRef.current = null;
  }, [clientId]);

  const syncApiReports = React.useCallback(async () => {
    try {
      const firstPage = await getClientLabReports(clientId, { page: 1, limit: 100 });
      let mergedReports = [...firstPage.reports];
      const totalPages = Math.max(1, firstPage.pagination.totalPages || 1);

      if (totalPages > 1) {
        const nextPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) =>
            getClientLabReports(clientId, { page: index + 2, limit: 100 })
          )
        );
        nextPages.forEach((pagePayload) => {
          mergedReports = mergedReports.concat(pagePayload.reports);
        });
      }

      const unique = new Map<string, ClientLabReportSummary>();
      mergedReports.forEach((item) => {
        unique.set(item.id, item);
      });

      const normalized = Array.from(unique.values()).sort((left, right) => {
        const leftTime = Date.parse(left.testDate || left.uploadedAt || '') || 0;
        const rightTime = Date.parse(right.testDate || right.uploadedAt || '') || 0;
        return rightTime - leftTime;
      });
      setApiReports(normalized);
      const activeReportIds = new Set(normalized.map((item) => item.id));
      const clientPrefix = `${clientId}::`;

      compareEnrichedReportsRef.current.forEach((reportId) => {
        if (!activeReportIds.has(reportId)) {
          compareEnrichedReportsRef.current.delete(reportId);
        }
      });
      compareEnrichedReportsSession.forEach((sessionKey) => {
        if (!sessionKey.startsWith(clientPrefix)) return;
        const [, reportId] = sessionKey.split('::');
        if (!activeReportIds.has(reportId)) {
          compareEnrichedReportsSession.delete(sessionKey);
        }
      });

      for (const key of reportBiomarkersSessionCache.keys()) {
        if (!key.startsWith(clientPrefix)) continue;
        const [, reportId] = key.split('::');
        if (!activeReportIds.has(reportId)) {
          reportBiomarkersSessionCache.delete(key);
          reportBiomarkersSessionInFlight.delete(key);
        }
      }

      for (const key of compareBiomarkersSessionCache.keys()) {
        if (!key.startsWith(clientPrefix)) continue;
        const [, leftReportId, rightReportId] = key.split('::');
        if (!activeReportIds.has(leftReportId) || !activeReportIds.has(rightReportId)) {
          compareBiomarkersSessionCache.delete(key);
          compareBiomarkersSessionInFlight.delete(key);
        }
      }
      setReportBiomarkersById((previous) => {
        const next: Record<string, Biomarker[]> = {};
        normalized.forEach((item) => {
          const sessionReportKey = buildReportSessionKey(clientId, item.id);
          if (previous[item.id]) next[item.id] = previous[item.id];
          else if (reportBiomarkersSessionCache.has(sessionReportKey)) {
            next[item.id] = reportBiomarkersSessionCache.get(sessionReportKey) as Biomarker[];
          }

          if (compareEnrichedReportsSession.has(sessionReportKey)) {
            compareEnrichedReportsRef.current.add(item.id);
          }
        });
        return next;
      });
    } catch (error) {
      showErrorToast(error, 'Failed to load lab reports');
    }
  }, [clientId]);

  React.useEffect(() => {
    void syncApiReports();
  }, [syncApiReports]);

  const loadReportBiomarkers = React.useCallback(async (
    reportId: string,
    force = false,
    enrichFromCompare = false
  ): Promise<Biomarker[]> => {
    const apiReport = apiReportsById.get(reportId);
    if (!apiReport) {
      return [];
    }

    const reportSessionKey = buildReportSessionKey(clientId, reportId);
    const reportTimestamp = getReportTimestamp(apiReport);
    const earliestReportTimestamp = apiReports.reduce((earliest, report) => {
      const timestamp = getReportTimestamp(report);
      if (timestamp <= 0) return earliest;
      if (earliest <= 0) return timestamp;
      return Math.min(earliest, timestamp);
    }, 0);
    const isEarliestReportByDate = reportTimestamp > 0
      && earliestReportTimestamp > 0
      && reportTimestamp === earliestReportTimestamp;
    const existing = reportBiomarkersByIdRef.current[reportId];
    const sessionCached = reportBiomarkersSessionCache.get(reportSessionKey);
    if (!existing && sessionCached) {
      setReportBiomarkersById((previous) => (
        previous[reportId]
          ? previous
          : {
            ...previous,
            [reportId]: sessionCached,
          }
      ));
    }

    if (force) {
      compareEnrichedReportsRef.current.delete(reportId);
      compareEnrichedReportsSession.delete(reportSessionKey);
    }

    const hasCompareEnrichment = compareEnrichedReportsRef.current.has(reportId)
      || compareEnrichedReportsSession.has(reportSessionKey);
    const shouldEnrichFromCompare = enrichFromCompare && !hasCompareEnrichment;

    const resolvedExisting = Array.isArray(existing) ? existing : sessionCached;
    if (!force && Array.isArray(resolvedExisting) && !shouldEnrichFromCompare) {
      return resolvedExisting;
    }

    const enrichWithCompare = async (rows: Biomarker[]): Promise<Biomarker[]> => {
      if (!enrichFromCompare || rows.length === 0) {
        return rows;
      }

      const previousReportId = getPreviousReportId(reportId);
      if (!previousReportId) {
        compareEnrichedReportsRef.current.add(reportId);
        compareEnrichedReportsSession.add(reportSessionKey);
        return rows;
      }

      try {
        const compareItems = await loadCompareBiomarkers(previousReportId, reportId);
        const enrichedRows = enrichBiomarkersWithCompareData(rows, compareItems);
        compareEnrichedReportsRef.current.add(reportId);
        compareEnrichedReportsSession.add(reportSessionKey);
        return enrichedRows;
      } catch {
        // Keep base biomarker payload if compare data is unavailable.
        compareEnrichedReportsRef.current.add(reportId);
        compareEnrichedReportsSession.add(reportSessionKey);
        return rows;
      }
    };

    if (!force && Array.isArray(resolvedExisting) && shouldEnrichFromCompare) {
      const enrichedExisting = await enrichWithCompare(resolvedExisting);
      reportBiomarkersSessionCache.set(reportSessionKey, enrichedExisting);
      setReportBiomarkersById((previous) => ({
        ...previous,
        [reportId]: enrichedExisting,
      }));
      return enrichedExisting;
    }

    const inFlight = reportBiomarkersSessionInFlight.get(reportSessionKey)
      || reportBiomarkersRequestRef.current.get(reportId);
    if (inFlight) {
      reportBiomarkersRequestRef.current.set(reportId, inFlight);
      return inFlight;
    }

    const request = (async () => {
      const detail = await getClientLabReport(clientId, reportId, { force });
      const reportMeta = detail.report || apiReport;
      let mapped = detail.biomarkers
        .map((item) => mapApiBiomarkerToClientBiomarker(item, reportMeta, clientId))
        .filter((item): item is Biomarker => Boolean(item))
        .sort((left, right) => {
          const leftTime = Date.parse(left.testedDate || '') || 0;
          const rightTime = Date.parse(right.testedDate || '') || 0;
          return rightTime - leftTime;
        });

      mapped = await enrichWithCompare(mapped);

      const hasPayloadTrendData = mapped.some((row) => hasBiomarkerTrendPayload(row));
      if (isEarliestReportByDate && !hasPayloadTrendData) {
        mapped = mapped.map((row) => {
          const source = row as BiomarkerTrendRecord;
          const sanitized: BiomarkerTrendRecord = {
            ...source,
            previousValue: null,
            previousValueText: null,
            previousUnit: source.unit || '',
            changePercent: null,
            changeDirection: null,
            hasPreviousFromApi: false,
            hasChangePercentFromApi: false,
            trendValues: [source.value],
            trendDates: source.testedDate ? [source.testedDate] : undefined,
          };
          return sanitized as unknown as Biomarker;
        });
      }

      reportBiomarkersSessionCache.set(reportSessionKey, mapped);

      setReportBiomarkersById((previous) => ({
        ...previous,
        [reportId]: mapped,
      }));
      return mapped;
    })();

    reportBiomarkersRequestRef.current.set(reportId, request);
    reportBiomarkersSessionInFlight.set(reportSessionKey, request);
    try {
      return await request;
    } finally {
      reportBiomarkersRequestRef.current.delete(reportId);
      reportBiomarkersSessionInFlight.delete(reportSessionKey);
    }
  }, [apiReportsById, clientId, getPreviousReportId, loadCompareBiomarkers]);

  const loadAllReportsBiomarkers = React.useCallback(async (): Promise<Biomarker[]> => {
    const aggregated = await getClientLabReportsAllBiomarkers(clientId);
    const reportsById = new Map(aggregated.reports.map((report) => [report.id, report]));

    const mapped = aggregated.biomarkers
      .map((item) => {
        const reportId = item.current?.reportId || null;
        if (!reportId) return null;
        const report = reportsById.get(reportId);
        if (!report) return null;
        return mapApiBiomarkerToClientBiomarker(item, report, clientId);
      })
      .filter((item): item is Biomarker => Boolean(item))
      .sort((left, right) => {
        const leftTime = Date.parse(left.testedDate || '') || 0;
        const rightTime = Date.parse(right.testedDate || '') || 0;
        return rightTime - leftTime;
      });

    setAllReportsBiomarkers(mapped);
    return mapped;
  }, [clientId]);

  const derivedApiReports = React.useMemo<DerivedLabReport[]>(() => {
    return apiReports
      .map((report) => {
        const biomarkersForReport = reportBiomarkersById[report.id] || [];
        const previewTagNames = Array.isArray(report.biomarkerPreview)
          ? Array.from(
            new Set(
              report.biomarkerPreview
                .map((item) => normalizeBiomarkerName(item.biomarkerName || item.biomarkerId))
                .filter(Boolean)
            )
          ).slice(0, 6)
          : [];
        const tagNames = biomarkersForReport.length > 0
          ? Array.from(new Set(biomarkersForReport.map((item) => getBiomarkerDisplayName(item)).filter(Boolean))).slice(0, 4)
          : previewTagNames;
        const reviewState: DerivedLabReport['reviewState'] = biomarkersForReport.length > 0
          ? (biomarkersForReport.some((item) => isBiomarkerAbnormal(item.status)) ? 'attention' : 'reviewed')
          : (isAttentionFromStatusCounts(report.statusCounts) ? 'attention' : 'reviewed');

        return {
          id: report.id,
          reportName: report.reportName,
          labName: report.labName ?? null,
          testedDate: report.testDate,
          biomarkers: biomarkersForReport,
          biomarkerCount: report.biomarkerCount ?? 0,
          biomarkerPreviewRemainingCount: report.biomarkerPreviewRemainingCount ?? 0,
          tagNames,
          reviewState,
          notes: report.notes ?? null,
        };
      })
      .sort((left, right) => compareReportDatesDesc(left, right));
  }, [apiReports, reportBiomarkersById]);

  const visibleReports = derivedApiReports;

  const biomarkerReportSelectOptions = React.useMemo(
    () => [
      { value: 'all', label: 'All report biomarkers' },
      ...visibleReports.map((report) => ({
        value: report.id,
        label: report.reportName,
      })),
    ],
    [visibleReports],
  );

  const clearLocalLabReportCaches = React.useCallback(() => {
    reportBiomarkersRequestRef.current.clear();
    compareReportRequestRef.current.clear();
    compareReportCacheRef.current.clear();
    compareEnrichedReportsRef.current.clear();
    biomarkerPanelLoadGateRef.current.clear();
    biomarkerPanelLoadSignatureRef.current = null;
    reportBiomarkersSessionCache.clear();
    reportBiomarkersSessionInFlight.clear();
    compareBiomarkersSessionCache.clear();
    compareBiomarkersSessionInFlight.clear();
    compareEnrichedReportsSession.clear();
    setReportBiomarkersById({});
    setAllReportsBiomarkers([]);
  }, []);

  const refreshSelectedReportFromApi = React.useCallback((reportId: string) => {
    clearLocalLabReportCaches();
    lastReportSelectionFetchRef.current = reportId;
    if (reportId === 'all') {
      void loadAllReportsBiomarkers();
      return;
    }
    void loadReportBiomarkers(reportId, true, false);
  }, [clearLocalLabReportCaches, loadAllReportsBiomarkers, loadReportBiomarkers]);

  const handleLabReportsTabClick = React.useCallback(() => {
    setActiveSubview('lab-reports');
    clearLocalLabReportCaches();
    lastReportSelectionFetchRef.current = null;
    void syncApiReports();
  }, [clearLocalLabReportCaches, syncApiReports]);

  const handleBiomarkerPanelTabClick = React.useCallback(() => {
    setActiveSubview('biomarker-panel');
    clearLocalLabReportCaches();
    void syncApiReports();
    const reportIdToRefresh = selectedReportId || visibleReports[0]?.id || '';
    if (!reportIdToRefresh) return;
    if (!selectedReportId) {
      setSelectedReportId(reportIdToRefresh);
    }
    refreshSelectedReportFromApi(reportIdToRefresh);
  }, [clearLocalLabReportCaches, refreshSelectedReportFromApi, selectedReportId, syncApiReports, visibleReports]);

  React.useEffect(() => {
    if (
      selectedReportId
      && selectedReportId !== 'all'
      && !visibleReports.some((report) => report.id === selectedReportId)
    ) {
      setSelectedReportId('');
    }
  }, [visibleReports, selectedReportId]);

  React.useEffect(() => {
    if (selectedReportId || visibleReports.length === 0) return;
    // Default to the most recent available uploaded report.
    setSelectedReportId(visibleReports[0].id);
  }, [selectedReportId, visibleReports]);

  React.useEffect(() => {
    if (!isExtracting) {
      setExtractionProgressPercent(0);
      return;
    }

    let stepIndex = 0;
    setExtractionProgressPercent(EXTRACTION_PROGRESS_STEPS[stepIndex]);

    const timerId = window.setInterval(() => {
      if (stepIndex >= EXTRACTION_PROGRESS_STEPS.length - 1) {
        window.clearInterval(timerId);
        return;
      }
      stepIndex += 1;
      setExtractionProgressPercent(EXTRACTION_PROGRESS_STEPS[stepIndex]);
      if (stepIndex >= EXTRACTION_PROGRESS_STEPS.length - 1) {
        window.clearInterval(timerId);
      }
    }, EXTRACTION_PROGRESS_STEP_INTERVAL_MS);

    return () => window.clearInterval(timerId);
  }, [isExtracting]);

  const filteredReports = React.useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return visibleReports.filter((report) => {
      if (!normalizedQuery) return true;

      const haystack = [
        report.reportName,
        report.labName ?? '',
        formatReportDate(report.testedDate),
        report.tagNames.join(' '),
      ].join(' ').toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [visibleReports, searchQuery]);

  const latestReport = filteredReports[0] ?? null;

  const selectedBiomarkers = React.useMemo(() => {
    if (selectedReportId === 'all') return allReportsBiomarkers;
    if (!selectedReportId) return [];
    return visibleReports.find((report) => report.id === selectedReportId)?.biomarkers ?? [];
  }, [allReportsBiomarkers, selectedReportId, visibleReports]);

  const filteredBiomarkers = React.useMemo(() => {
    const normalizedQuery = biomarkerSearchQuery.trim().toLowerCase();
    if (!normalizedQuery) return selectedBiomarkers;

    return selectedBiomarkers.filter((item) => {
      const haystack = [
        getBiomarkerDisplayName(item),
        item.biomarkerId || '',
        item.status || '',
        item.unit || '',
        item.labName || '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [biomarkerSearchQuery, selectedBiomarkers]);

  const biomarkerPanelLabel = React.useMemo(() => {
    if (selectedReportId === 'all') return 'All report biomarkers';
    if (!selectedReportId) return 'Select report';
    const selected = visibleReports.find((report) => report.id === selectedReportId);
    if (!selected) return 'Lab Panel';
    return selected.labName || selected.reportName || 'Lab Panel';
  }, [selectedReportId, visibleReports]);

  const biomarkerPanelDateLabel = React.useMemo(() => {
    if (selectedReportId === 'all') {
      const parsed = visibleReports
        .map((report) => Date.parse(report.testedDate || ''))
        .filter((value) => Number.isFinite(value)) as number[];
      if (parsed.length === 0) return 'Unknown date';

      const min = Math.min(...parsed);
      const max = Math.max(...parsed);
      const minLabel = formatReportDate(new Date(min).toISOString());
      const maxLabel = formatReportDate(new Date(max).toISOString());
      return min === max ? minLabel : `${minLabel} - ${maxLabel}`;
    }
    if (!selectedReportId) return 'Select report';
    const selected = visibleReports.find((report) => report.id === selectedReportId);
    return formatReportDate(selected?.testedDate);
  }, [selectedReportId, visibleReports]);

  React.useEffect(() => {
    if (activeSubview !== 'biomarker-panel' || apiReports.length === 0) return;

    const run = async () => {
      try {
        if (!selectedReportId) {
          lastReportSelectionFetchRef.current = null;
          if (!selectReportErrorShownRef.current) {
            selectReportErrorShownRef.current = true;
            showErrorToast('Select a report to view biomarkers.');
          }
          return;
        }
        selectReportErrorShownRef.current = false;
        if (lastReportSelectionFetchRef.current === selectedReportId) return;
        lastReportSelectionFetchRef.current = selectedReportId;

        if (selectedReportId === 'all') {
          const allGateKey = 'all_reports:force';
          if (biomarkerPanelLoadSignatureRef.current === allGateKey) return;
          if (biomarkerPanelLoadGateRef.current.has(allGateKey)) return;
          biomarkerPanelLoadSignatureRef.current = allGateKey;
          biomarkerPanelLoadGateRef.current.add(allGateKey);
          try {
            await loadAllReportsBiomarkers();
          } finally {
            biomarkerPanelLoadGateRef.current.delete(allGateKey);
          }
          return;
        }

        if (!apiReportsById.has(selectedReportId)) return;
        const selectedGateKey = `single:${selectedReportId}:force`;
        if (biomarkerPanelLoadSignatureRef.current === selectedGateKey) return;
        if (biomarkerPanelLoadGateRef.current.has(selectedGateKey)) return;
        biomarkerPanelLoadSignatureRef.current = selectedGateKey;
        biomarkerPanelLoadGateRef.current.add(selectedGateKey);
        try {
          // Always force fetch when selected report changes.
          await loadReportBiomarkers(selectedReportId, true, false);
        } finally {
          biomarkerPanelLoadGateRef.current.delete(selectedGateKey);
        }
      } catch (error) {
        biomarkerPanelLoadSignatureRef.current = null;
        lastReportSelectionFetchRef.current = null;
        showErrorToast(error, 'Failed to load biomarker panel');
      }
    };

    void run();
  }, [activeSubview, apiReports, apiReportsById, loadAllReportsBiomarkers, loadReportBiomarkers, selectedReportId]);

  const exportButtonSx = React.useMemo(
    () => ({
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      fontSize: '0.8125rem',
      textTransform: 'none',
      height: { xs: 32, sm: 34 },
      px: 1.6,
      borderRadius: '12px',
      border: '1px solid #d0d5dd',
      bgcolor: '#fff',
      color: '#344054',
      '&:hover': {
        bgcolor: '#f8fafc',
        borderColor: '#b8c4d1',
      },
      '&.Mui-disabled': {
        bgcolor: '#fff',
        opacity: 1,
        color: '#98a2b3',
        borderColor: '#eaecf0',
      },
    }),
    []
  );

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const processSelectedFiles = React.useCallback((files: File[]) => {
    const error = validateLabReportFiles(files);
    if (error) {
      setSelectedFiles([]);
      setValidationMessage(error);
      return;
    }

    setValidationMessage(null);
    setSelectedFiles(files);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    processSelectedFiles(event.target.files ? Array.from(event.target.files) : []);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);

    if (isExtracting) return;
    processSelectedFiles(event.dataTransfer.files ? Array.from(event.dataTransfer.files) : []);
  };

  const handleClear = () => {
    setSelectedFiles([]);
    setValidationMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExtract = async () => {
    const error = validateLabReportFiles(selectedFiles);
    if (error) {
      setValidationMessage(error);
      showErrorToast(error, 'Invalid file');
      return;
    }
    if (!selectedFiles.length) return;

    setIsExtracting(true);
    setExtractionProgress('uploading');
    setValidationMessage(null);
    try {
      const response = await extractClientLabReportMulti(clientId, selectedFiles, {
        onProgress: (state) => setExtractionProgress(state),
      });
      const skippedPages = response.storage.skippedPages ?? response.storage.partialExtraction?.skippedPages ?? [];
      if (skippedPages.length > 0) {
        showWarningToast(
          'Lab report partially processed',
          `Biomarkers were extracted, but page${skippedPages.length === 1 ? '' : 's'} ${skippedPages.join(', ')} could not be read. Please verify the extracted values against the report.`,
          { duration: 10000 }
        );
      } else {
        showSuccessToast('Lab report processed', response.message || 'Biomarkers extracted successfully.');
      }
      handleClear();
      await onRefresh?.();
      await syncApiReports();
    } catch (error) {
      showErrorToast(error, 'Failed to upload/extract lab report');
    } finally {
      setIsExtracting(false);
      setExtractionProgress(null);
    }
  };

  const openUrlInNewTab = (url: string, previewWindow?: Window | null): void => {
    if (previewWindow && !previewWindow.closed) {
      previewWindow.opener = null;
      previewWindow.location.replace(url);
      previewWindow.focus();
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const createPreviewWindow = (): Window | null => {
    const previewWindow = window.open('about:blank', '_blank');
    if (previewWindow) {
      previewWindow.opener = null;
    }
    return previewWindow;
  };

  const triggerFileDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buildReportFileName = (report: DerivedLabReport, fileIndex: number, fileCount: number): string => {
    const suffix = fileCount > 1 ? `-${fileIndex + 1}` : '';
    return `${report.reportName || 'lab-report'}${suffix}`;
  };

  const handleViewReportFile = React.useCallback(async (report: DerivedLabReport) => {
    const previewWindow = createPreviewWindow();
    try {
      const { downloadUrl } = await getClientLabReportDownloadUrl(clientId, report.id, {
        disposition: 'inline',
        fileIndex: 0,
      });
      openUrlInNewTab(downloadUrl, previewWindow);
    } catch (error) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      showErrorToast(error, 'Failed to open lab report');
    }
  }, [clientId]);

  const handleDownloadAllReportFiles = React.useCallback(async () => {
    if (!filteredReports.length) return;
    try {
      for (let i = 0; i < filteredReports.length; i++) {
        const report = filteredReports[i];
        const apiReport = apiReportsById.get(report.id);
        const fileCount = apiReport?.fileCount ?? 1;
        for (let fileIndex = 0; fileIndex < fileCount; fileIndex++) {
          const { downloadUrl } = await getClientLabReportDownloadUrl(clientId, report.id, {
            disposition: 'attachment',
            fileIndex,
          });
          triggerFileDownload(downloadUrl, buildReportFileName(report, fileIndex, fileCount));
          if (i < filteredReports.length - 1 || fileIndex < fileCount - 1) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }
    } catch (error) {
      showErrorToast(error, 'Failed to download lab reports');
    }
  }, [clientId, filteredReports, apiReportsById]);

  const handleDownloadReportFile = React.useCallback(async (report: DerivedLabReport) => {
    try {
      const apiReport = apiReportsById.get(report.id);
      const fileCount = apiReport?.fileCount ?? 1;
      for (let fileIndex = 0; fileIndex < fileCount; fileIndex++) {
        const { downloadUrl } = await getClientLabReportDownloadUrl(clientId, report.id, {
          disposition: 'attachment',
          fileIndex,
        });
        triggerFileDownload(downloadUrl, buildReportFileName(report, fileIndex, fileCount));
        if (fileIndex < fileCount - 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      showErrorToast(error, 'Failed to download lab report');
    }
  }, [clientId, apiReportsById]);

  const handleExportSingleReport = React.useCallback(async (report: DerivedLabReport) => {
    let biomarkerRows = report.biomarkers;
    if (!biomarkerRows.length) {
      try {
        biomarkerRows = await loadReportBiomarkers(report.id);
      } catch (error) {
        showErrorToast(error, 'Failed to load report biomarkers');
        return;
      }
    }

    if (!biomarkerRows.length) {
      showWarningToast('No biomarker data found for this report.');
      return;
    }

    const rows: Array<Array<unknown>> = [
      ['Biomarker', 'Value', 'Unit', 'Status', 'Range Source', 'Range Value', 'Test Date'],
      ...biomarkerRows.map((item) => {
        const rangeSource = getRangeSource(item);
        return [
          getBiomarkerDisplayName(item),
          item.value,
          item.unit,
          item.status || 'unknown',
          rangeSource.label,
          getRangeValueText(item),
          formatReportDate(item.testedDate),
        ];
      }),
    ];

    const fileBase = report.reportName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'lab-report';
    const dateStamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`${fileBase}-${dateStamp}.csv`, rows);
  }, [loadReportBiomarkers]);

  const handleExportBiomarkerPanel = React.useCallback(async () => {
    if (!selectedBiomarkers.length) {
      showWarningToast('No biomarker data available to export.');
      return;
    }

    const rows = selectedBiomarkers.map((item) => {
      const trendRecord = item as BiomarkerTrendRecord;

      const hasPreviousValue = trendRecord.hasPreviousFromApi === true
        && (
          (typeof trendRecord.previousValue === 'number' && Number.isFinite(trendRecord.previousValue))
          || Boolean(trendRecord.previousValueText?.trim())
        );

      const previousRaw = trendRecord.previousValueText?.trim()
        ? trendRecord.previousValueText.trim()
        : (
          typeof trendRecord.previousValue === 'number' && Number.isFinite(trendRecord.previousValue)
            ? String(Number(trendRecord.previousValue.toFixed(4)))
            : ''
        );

      const previousUnit = (trendRecord.previousUnit || item.unit || '').trim();
      const previousRawHasUnit = /[a-zA-Z%]/.test(previousRaw);
      const previous = hasPreviousValue
        ? `${previousRaw}${previousUnit && !previousRawHasUnit ? ` ${previousUnit}` : ''}`.trim()
        : '—';

      const deltaPercent = trendRecord.hasChangePercentFromApi === true
        && typeof trendRecord.changePercent === 'number'
        && Number.isFinite(trendRecord.changePercent)
        ? trendRecord.changePercent
        : null;
      const direction = (trendRecord.changeDirection || '').trim().toLowerCase();
      const improving = deltaPercent == null
        ? null
        : (
          direction.includes('down')
          || direction.includes('decrease')
          || direction.includes('lower')
          || (!direction && deltaPercent <= 0)
        );
      const roundedDeltaPercent = deltaPercent == null ? null : Math.round(Math.abs(deltaPercent) * 10) / 10;
      const showDeltaArrow = roundedDeltaPercent != null && roundedDeltaPercent > 0;
      const change = roundedDeltaPercent == null
        ? '—'
        : `${showDeltaArrow ? (improving ? '↘ ' : '↗ ') : ''}${roundedDeltaPercent}%`;

      const trendValues = Array.isArray(trendRecord.trendValues)
        ? trendRecord.trendValues.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
        : [];
      const formattedTrendValues = trendValues.map((value) => String(Number(value.toFixed(4))));
      const trend = formattedTrendValues.length === 0
        ? '—'
        : (
          formattedTrendValues.length > 6
            ? `${formattedTrendValues.slice(-6).join(' -> ')} (latest 6)`
            : formattedTrendValues.join(' -> ')
        );

      return {
        biomarker: getBiomarkerDisplayName(item),
        current: formatMeasuredValue(item.value, item.unit),
        refRange: getRangeValueText(item),
        status: item.status || 'unknown',
        previous,
        change,
        trend,
      };
    });

    try {
      const { downloadBiomarkerPanelPdf } = await import('@/clients/details/shared/utils/biomarkerPanelExportPdf');
      await downloadBiomarkerPanelPdf({
        clientId,
        clientName,
        panelLabel: biomarkerPanelLabel,
        panelDateLabel: biomarkerPanelDateLabel,
        rows,
      });
      showSuccessToast('Biomarker panel exported as PDF');
    } catch (error) {
      showErrorToast(error, 'Failed to export biomarker panel PDF');
    }
  }, [biomarkerPanelDateLabel, biomarkerPanelLabel, clientId, clientName, selectedBiomarkers]);

  const handleExportAllReports = React.useCallback(() => {
    const rows: Array<Array<unknown>> = [
      ['Report Name', 'Lab Name', 'Test Date', 'Status', 'Biomarker Count', 'Biomarkers'],
      ...filteredReports.map((report) => [
        report.reportName,
        report.labName || 'N/A',
        formatReportDate(report.testedDate),
        report.reviewState,
        report.biomarkerCount,
        report.tagNames.join(' | '),
      ]),
    ];

    const dateStamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`client-lab-reports-${clientId}-${dateStamp}.csv`, rows);
    showSuccessToast('Lab reports exported');
  }, [clientId, filteredReports]);

  const handleDeleteReport = React.useCallback(async (report: DerivedLabReport) => {
    setIsDeletingReport(true);
    try {
      const response = await deleteClientLabReport(clientId, report.id);
      showSuccessToast(response.message || 'Lab report deleted.');
      const reportSessionKey = buildReportSessionKey(clientId, report.id);
      reportBiomarkersSessionCache.delete(reportSessionKey);
      reportBiomarkersSessionInFlight.delete(reportSessionKey);
      compareEnrichedReportsSession.delete(reportSessionKey);
      for (const key of compareBiomarkersSessionCache.keys()) {
        if (!key.startsWith(`${clientId}::`)) continue;
        const [, leftReportId, rightReportId] = key.split('::');
        if (leftReportId === report.id || rightReportId === report.id) {
          compareBiomarkersSessionCache.delete(key);
          compareBiomarkersSessionInFlight.delete(key);
        }
      }
      setApiReports((previous) => previous.filter((item) => item.id !== report.id));
      setReportBiomarkersById((previous) => {
        const next = { ...previous };
        delete next[report.id];
        return next;
      });
      if (selectedReportId === report.id) {
        setSelectedReportId('all');
      }
      setDeleteConfirmReport(null);
      await onRefresh?.();
    } catch (error) {
      showErrorToast(error, 'Failed to delete lab report');
    } finally {
      setIsDeletingReport(false);
    }
  }, [clientId, onRefresh, selectedReportId]);

  const closeDeleteConfirmDialog = React.useCallback(() => {
    if (isDeletingReport) return;
    setDeleteConfirmReport(null);
  }, [isDeletingReport]);

  const handleOpenNotesModal = React.useCallback((report: DerivedLabReport) => {
    setNotesText(report.notes || '');
    setNotesModalReport(report);
  }, []);

  const handleSaveNotes = React.useCallback(async () => {
    if (!notesModalReport) return;
    setNotesSaving(true);
    try {
      await updateClientLabReportNotes(clientId, {
        reportId: notesModalReport.id,
        notes: notesText.trim() || null,
      });
      showNotesSavedToast('lab', notesText.trim() || null);
      setNotesModalReport(null);
      await syncApiReports();
    } catch (error) {
      showErrorToast(error, 'Failed to save notes');
    } finally {
      setNotesSaving(false);
    }
  }, [clientId, notesModalReport, notesText, syncApiReports]);

  const renderReportCard = (report: DerivedLabReport) => {
    const dateParts = getDateBadgeParts(report.testedDate);
    const reportNotes = typeof report.notes === 'string' ? report.notes.trim() : '';
    const hasReportNotes = Boolean(reportNotes);
    const biomarkerNames = report.biomarkers.length > 0
      ? Array.from(new Set(report.biomarkers.map((item) => getBiomarkerDisplayName(item)).filter(Boolean)))
      : [];
    const allTags = biomarkerNames.length > 0 ? biomarkerNames : report.tagNames;
    const chipTags = allTags;

    return (
      <div
        key={report.id}
        className="grid items-start gap-3"
        style={{ gridTemplateColumns: '74px minmax(0, 1fr)' }}
      >
        <div className={cn('flex flex-col justify-center text-center', hasReportNotes ? 'self-start pt-2' : 'self-center')}>
          <p className="text-[2.1rem] font-bold leading-[0.95] text-[#344054]">{dateParts.day}</p>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.06em] text-[#98a2b3]">{dateParts.month}</p>
        </div>

        <article
          className="cursor-pointer overflow-hidden rounded-xl border border-[#e4e7ec] bg-white transition-colors hover:border-[#b9e6f5] hover:bg-[#fafcfe]"
          onClick={() => { void handleViewReportFile(report); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              void handleViewReportFile(report);
            }
          }}
          role="link"
          tabIndex={0}
          aria-label={`Open ${report.reportName} in a new tab`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#eaecf0] px-6 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <img src={documentIcon} alt="" aria-hidden className="h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <button
                  type="button"
                  className="block w-full text-left text-[1.02rem] font-bold leading-tight text-[#101828] break-words hover:text-[#1B94C5] focus:outline-none focus-visible:underline"
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleViewReportFile(report);
                  }}
                >
                  {report.reportName}
                </button>
                {hasReportNotes && (
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-[#667085]">
                    {reportNotes}
                  </p>
                )}
              </div>
            </div>

            <div
              className="flex items-center gap-6"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <span className="rounded-full bg-[#E8F7F9] px-4 py-1 text-xs font-semibold leading-[12px] text-[#29A7D8]">
                {formatReportDate(report.testedDate)}
              </span>
              <Button
                variant="ghost"
                className="h-6 px-1 text-xs font-semibold text-[#29A7D8] hover:bg-transparent hover:text-[#1B94C5]"
                onClick={() => handleOpenNotesModal(report)}
              >
                Add a Note
                <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                className="h-6 px-1 text-xs font-semibold text-[#29A7D8] hover:bg-transparent hover:text-[#1B94C5]"
                onClick={() => {
                  setSelectedReportId(report.id);
                  setActiveSubview('biomarker-panel');
                  refreshSelectedReportFromApi(report.id);
                }}
              >
                View
                <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-[#344054] hover:bg-[#f2f4f7]"
                onClick={() => { void handleDownloadReportFile(report); }}
                title="Download report file"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-[#fda29b] hover:bg-[#fff1f3] hover:text-[#f97066]"
                onClick={() => setDeleteConfirmReport(report)}
                title="Delete report"
                aria-label={`Delete ${report.reportName || 'lab report'}`}
              >
                <img src={trashBinIcon} alt="" aria-hidden className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="bg-[#fcfcfd] px-6 py-4">
            <div className="flex flex-wrap items-center gap-2">
              {chipTags.map((tag, idx) => (
                <span
                  key={`${report.id}-tag-${idx}`}
                  className="rounded-full bg-[#F2FAFD] px-5 py-1.5 text-xs font-semibold leading-[12px] text-[#4b6b7d]"
                >
                  {formatUnderscoreDisplayLabel(tag)}
                </span>
              ))}
              {report.biomarkerPreviewRemainingCount > 0 && (
                <span className="rounded-full bg-[#F2FAFD] px-5 py-1.5 text-xs font-semibold leading-[12px] text-[#4b6b7d]">
                  {report.biomarkerPreviewRemainingCount}+ more
                </span>
              )}
            </div>
          </div>
        </article>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {activeSubview === 'lab-reports' ? (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={LAB_REPORT_ALLOWED_MIME_TYPES.join(',')}
            multiple
            onChange={handleFileChange}
          />

          <div
            onDragEnter={(event) => {
              event.preventDefault();
              if (!isExtracting) setIsDragActive(true);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (!isExtracting) setIsDragActive(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setIsDragActive(false);
            }}
            onDrop={handleDrop}
            className={cn(
              'rounded-2xl border-2 border-solid px-6 py-10 text-center transition-colors',
              isDragActive
                ? 'border-[#20AEDC] bg-[#f0f9ff]'
                : 'border-[#20AEDC40] bg-[#fafcfe]'
            )}
            style={{ borderTop: '2px solid #20AEDC40' }}
          >
            <UploadCloud className="mx-auto h-8 w-8 text-[#98a2b3]" />
            <p className="mt-4 text-base font-bold text-[#1d2939]">Drop PDFs or images here</p>
            <p className="mt-1 text-sm text-[#667085]">
              or{' '}
              {isExtracting ? (
                <span className="font-semibold text-[#98a2b3]">processing files</span>
              ) : (
                <button
                  type="button"
                  className="font-semibold text-[#2EC3B6] hover:text-[#23a99d]"
                  onClick={handlePickFile}
                >
                  browse files
                </button>
              )}
            </p>
          </div>

          {selectedFiles.length > 0 && (
            <div className="mt-4 rounded-lg border border-[#e4e7ec] bg-[#f9fafb] px-3 py-2 text-sm text-[#344054]">
              {selectedFiles.map((file, idx) => (
                <div key={idx} className={idx > 0 ? 'mt-1' : undefined}>
                  <span className="font-medium">{file.name}</span>
                  <span className="ml-2 text-[#667085]">({formatBytes(file.size)})</span>
                </div>
              ))}
            </div>
          )}

          {isExtracting ? (
            <div className="mt-4 text-sm font-medium text-[#667085]">
              Upload controls are disabled while extraction is in progress.
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="border-[#d0d5dd] bg-white text-[#344054]"
                onClick={handlePickFile}
              >
                Select Files
              </Button>
              <Button
                type="button"
                className="bg-[#101828] text-white hover:bg-[#0c111d]"
                onClick={handleExtract}
                disabled={selectedFiles.length === 0}
              >
                Upload and Extract
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="text-[#475467]"
                onClick={handleClear}
                disabled={selectedFiles.length === 0}
              >
                Remove selected report
              </Button>
            </div>
          )}

          {isExtracting && extractionProgress && (
            <div className="mt-3 rounded-lg border border-[#e4e7ec] bg-white px-3 py-2 text-sm text-[#344054]">
              <div className="flex items-center justify-between gap-2">
                <span>{getExtractionProgressLabel(extractionProgress)}</span>
                <span className="text-xs font-semibold text-[#667085]">
                  {extractionProgressPercent}%
                </span>
              </div>
              <Progress
                value={extractionProgressPercent}
                className="mt-2 h-2 bg-[#eaecf0]"
                indicatorClassName="bg-[#12B76A]"
              />
            </div>
          )}

          {validationMessage && (
            <div className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
              {validationMessage}
            </div>
          )}

        </div>
      ) : null}

      <div className="mt-6 space-y-3 px-2">
        <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-6 border-b border-[#eaecf0]">
          <button
            type="button"
            onClick={handleLabReportsTabClick}
            className={cn(
              'inline-flex items-center gap-2 border-b-2 pb-1 text-sm font-medium leading-none transition-colors',
              activeSubview === 'lab-reports'
                ? 'border-[#22a7d6] text-[#22a7d6]'
                : 'border-transparent text-[#667085] hover:text-[#344054]'
            )}
          >
            Lab Reports
            <span className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              activeSubview === 'lab-reports' ? 'bg-[#22a7d6] text-white' : 'bg-[#f2f4f7] text-[#667085]'
            )}>
              {visibleReports.length}
            </span>
          </button>
          <button
            type="button"
            onClick={handleBiomarkerPanelTabClick}
            className={cn(
              'inline-flex items-center gap-2 border-b-2 pb-1 text-sm font-medium leading-none transition-colors',
              activeSubview === 'biomarker-panel'
                ? 'border-[#22a7d6] text-[#22a7d6]'
                : 'border-transparent text-[#667085] hover:text-[#344054]'
            )}
          >
            Bio-marker Panel
          </button>
        </div>

        {activeSubview === 'lab-reports' ? (
          <div className="flex flex-wrap items-center gap-2">
            <SlashSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
              sx={{
                width: { xs: '100%', sm: 280 },
                minWidth: { xs: 0, sm: 220 },
              }}
            />

            <MuiButton
              variant="outlined"
              onClick={() => { void handleDownloadAllReportFiles(); }}
              disabled={filteredReports.length === 0}
              endIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={exportButtonSx}
            >
              Export
            </MuiButton>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <ClientsFilterBar
              searchQuery={biomarkerSearchQuery}
              onSearchChange={handleBiomarkerSearchChange}
              onSearchClear={() => setBiomarkerSearchQuery('')}
              showFilterButton={false}
              showExportButton={false}
              enableSearchShortcut
            />
            <MuiButton
              variant="outlined"
              onClick={() => { void handleExportBiomarkerPanel(); }}
              disabled={selectedBiomarkers.length === 0}
              endIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={exportButtonSx}
            >
              Export
            </MuiButton>
          </div>
        )}
        </div>

      </div>

      <div className="mt-4">
      {activeSubview === 'lab-reports' ? (
        filteredReports.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f9fafb] px-4 py-10 text-center text-sm text-[#667085]">
            No lab reports found. Please upload the report to get biomarkers.
          </div>
        ) : (
          <div className="space-y-5">
            {latestReport ? (
              <section className="space-y-3">
                <h4 className="text-[16px] font-semibold leading-tight text-[#344054]">Latest Report</h4>
                {renderReportCard(latestReport)}
              </section>
            ) : null}

            <section className="space-y-3">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20AEDC] focus-visible:ring-offset-2"
                onClick={() => setIsAllReportsExpanded((previous) => !previous)}
                aria-expanded={isAllReportsExpanded}
                aria-controls="client-lab-all-reports-list"
              >
                <h4 className="text-[16px] font-semibold leading-tight text-[#344054]">All Reports</h4>
                <ChevronRight
                  className={cn(
                    'h-5 w-5 shrink-0 text-[#344054] transition-transform duration-150',
                    isAllReportsExpanded ? 'rotate-90' : 'rotate-0'
                  )}
                />
              </button>
              {isAllReportsExpanded ? (
                <div id="client-lab-all-reports-list" className="space-y-3">
                  {filteredReports.map((report) => renderReportCard(report))}
                </div>
              ) : null}
            </section>
          </div>
        )
      ) : (
        <BiomarkerTrendSection
          biomarkers={filteredBiomarkers}
          panelLabel={biomarkerPanelLabel}
          panelDateLabel={biomarkerPanelDateLabel}
          reportSelector={
            <SearchableSelect
              value={selectedReportId}
              onValueChange={setSelectedReportId}
              options={biomarkerReportSelectOptions}
              placeholder="Select report biomarkers"
              searchPlaceholder="Search reports..."
              emptyText="No reports found."
              disabled={biomarkerReportSelectOptions.length === 0}
              triggerClassName="h-10 w-[200px] sm:w-[240px] md:w-[300px] max-w-full text-left"
              itemClassName="max-w-[300px] truncate"
            />
          }
        />
      )}
      </div>

      {notesModalReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setNotesModalReport(null)}>
          <div
            className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#eaecf0] px-6 py-4">
              <div className="flex items-center gap-3">
                <img src={documentIcon} alt="" aria-hidden className="h-5 w-5 shrink-0" />
                <div>
                  <h3 className="text-sm font-bold text-[#101828]">Add Notes</h3>
                  <p className="text-xs text-[#667085]">
                    {clientName} · {notesModalReport.reportName}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setNotesModalReport(null)}
                className="rounded-lg p-1 text-[#667085] hover:bg-[#f2f4f7] hover:text-[#344054]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#344054]">
                Notes
              </label>
              <textarea
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                placeholder="Add your notes here...."
                rows={5}
                className="w-full resize-none rounded-xl border border-[#e4e7ec] bg-white px-4 py-3 text-sm text-[#344054] placeholder:text-[#98a2b3] focus:border-[#25a8da] focus:outline-none focus:ring-1 focus:ring-[#25a8da]"
              />
            </div>

            <div className="flex justify-end border-t border-[#eaecf0] px-6 py-4">
              <button
                type="button"
                disabled={notesSaving}
                onClick={() => { void handleSaveNotes(); }}
                className="inline-flex items-center gap-2 rounded-full bg-[#29A7D8] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#1B94C5] disabled:opacity-50"
              >
                {notesSaving ? 'Saving...' : 'Add a Note'}
                {!notesSaving && <span>→</span>}
              </button>
            </div>
          </div>
        </div>
      )}
      <AlertDialog
        open={Boolean(deleteConfirmReport)}
        onOpenChange={(open) => {
          if (!open) closeDeleteConfirmDialog();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete lab report?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-medium text-foreground">
                {deleteConfirmReport?.reportName || 'this lab report'}
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingReport}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingReport || !deleteConfirmReport}
              onClick={(event) => {
                event.preventDefault();
                if (deleteConfirmReport) {
                  void handleDeleteReport(deleteConfirmReport);
                }
              }}
            >
              {isDeletingReport ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
